import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  detailsOpen: boolean;
  copied: boolean;
}

/**
 * Top-level ErrorBoundary that wraps the entire <Outlet />.
 * Anything that throws during render in any route will fall back to a
 * dark, friendly card with reload + collapsible details + copy-button.
 *
 * The 64-person live workshop is the intended deployment target — we'd
 * rather show a card than a white screen.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = {
    error: null,
    errorInfo: null,
    detailsOpen: false,
    copied: false,
  };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
     
    console.error("ErrorBoundary caught:", error, errorInfo);
    this.setState({ errorInfo });
  }

  reload = () => {
    window.location.reload();
  };

  copyDetails = async () => {
    const { error, errorInfo } = this.state;
    const text = [
      `Error: ${error?.name ?? "Unknown"}: ${error?.message ?? ""}`,
      "",
      "Stack:",
      error?.stack ?? "(no stack)",
      "",
      "Component stack:",
      errorInfo?.componentStack ?? "(no component stack)",
      "",
      `URL: ${window.location.href}`,
      `UA:  ${navigator.userAgent}`,
      `Time: ${new Date().toISOString()}`,
    ].join("\n");
    try {
      await navigator.clipboard.writeText(text);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 1500);
    } catch {
      /* noop */
    }
  };

  toggleDetails = () => {
    this.setState((s) => ({ detailsOpen: !s.detailsOpen }));
  };

  render() {
    const { error, errorInfo, detailsOpen, copied } = this.state;

    if (!error) return this.props.children;

    return (
      <div className="error-boundary-root">
        <div className="deck-bg" aria-hidden="true" />
        <div className="error-boundary-card">
          <div className="deck-eyebrow" style={{ color: "var(--svo-blau)" }}>
            SVO Offsite · Fehler
          </div>
          <h1 className="deck-title" style={{ margin: "8px 0 4px", fontSize: "clamp(1.5rem, 3vw, 2rem)" }}>
            Da ist etwas <span className="accent">schiefgelaufen</span>.
          </h1>
          <p className="deck-sub" style={{ margin: "0 0 20px" }}>
            Die Anwendung konnte einen Bildschirm nicht zeichnen. Versuche zuerst
            ein Reload. Wenn das nicht hilft: Tab schließen und die Seite neu öffnen.
          </p>

          <div className="error-boundary-actions">
            <button
              type="button"
              onClick={this.reload}
              className="btn btn-primary btn-lg"
            >
              ↻ Seite neu laden
            </button>
            <button
              type="button"
              onClick={this.toggleDetails}
              className="btn btn-ghost"
              aria-expanded={detailsOpen}
            >
              {detailsOpen ? "▾ Fehler-Details ausblenden" : "▸ Fehler-Details zeigen"}
            </button>
          </div>

          {detailsOpen && (
            <div className="error-boundary-details">
              <div className="error-boundary-details-head">
                <code style={{ color: "var(--svo-error, #c53030)", fontWeight: 600 }}>
                  {error.name}: {error.message}
                </code>
                <button
                  type="button"
                  onClick={this.copyDetails}
                  className="btn btn-ghost"
                  style={{ padding: "4px 10px", fontSize: 13 }}
                >
                  {copied ? "✓ Kopiert" : "⎘ Kopieren"}
                </button>
              </div>
              <pre className="error-boundary-stack">
                {error.stack ?? "(no stack)"}
                {errorInfo?.componentStack
                  ? `\n\nComponent stack:${errorInfo.componentStack}`
                  : ""}
              </pre>
            </div>
          )}

          <p className="form-hint" style={{ marginTop: 24, opacity: 0.75 }}>
            Im Notfall: Tab schließen und neu öffnen — der Login bleibt erhalten.
          </p>
        </div>
      </div>
    );
  }
}
